import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
// Liste des tunnels — SOURCE UNIQUE, déjà utilisée par PWAInstallPrompt.
// On la RÉUTILISE plutôt que d'en écrire une seconde qui divergerait.
import { isTunnelPath } from "../utils/tunnelPaths";
import { safeLocalStorage } from "../utils/safeStorage";
import { CLE_CONSENTEMENT, lireConsentement, effacerCookiesMesure } from "../utils/consentementCookies";

const COOKIE_CONSENT_KEY = CLE_CONSENTEMENT;

// Met à jour le Consent Mode de Google (cf. index.html). granted=true
// autorise la MESURE D'AUDIENCE 1re partie ; false la coupe (aucun cookie GA).
// Path 1 (juillet 2026, rejet Apple 5.1.2(i)) : Gively ne fait AUCUN tracking
// publicitaire → on ne pilote QUE `analytics_storage` (plus de ad_storage /
// ad_user_data / ad_personalization). Cohérent avec la bannière « jamais de
// publicité ciblée », la Data Safety Play et le consentement CNIL.
//
// MODE DE BASE (29/10/2026) : gtag.js n'est chargé qu'APRÈS « Accepter »
// (`window.__givelyChargerGa`, défini dans index.html). Avant tout choix et
// après un refus, aucune requête ne part vers Google. Un refus efface aussi les
// cookies _ga d'une acceptation antérieure.
const applyConsent = (granted) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
    });
  }
  if (granted) {
    if (typeof window.__givelyChargerGa === "function") window.__givelyChargerGa();
  } else {
    effacerCookiesMesure();
  }
};

const BOUTON_CHOIX =
  "px-4 py-2 text-sm bg-[#1DA1F2] text-white rounded-lg hover:bg-[#0d8bd9] transition-colors font-medium";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();
  // PAS DE BANDEAU PAR-DESSUS UN TUNNEL (relevé du 23/09/2026).
  //
  // Ce bandeau est `fixed bottom-0 left-0 right-0` en z-index 9999, monté à la
  // RACINE (App.js), hors des routes, et sans aucune condition de chemin. La
  // barre d'action du tunnel de publication est elle aussi `fixed bottom-0`,
  // en z-index 1000 (pages/publish/components/PublishCTA.js:59-63). Recouvrement
  // géométrique total, 9999 > 1000 : le bandeau masquait « Continuer »,
  // « Publier » et « Retour » — c'est-à-dire TOUTE la navigation du tunnel.
  //
  // Le dépôt connaissait déjà ce piège et l'avait résolu pour l'AUTRE bannière :
  // PWAInstallPrompt.js:154 sort si `isTunnelPath(...)`, après une régression
  // identique (« la bannière recouvrait le CTA d'onboarding asso »). Celui-ci
  // n'avait pas d'équivalent. On réemploie donc la même liste et la même garde.
  //
  // CE N'EST PAS UN CONTOURNEMENT DU CONSENTEMENT : rien n'est déposé ni mesuré
  // avant réponse (`applyConsent` n'est appelé qu'au clic), et le bandeau
  // réapparaît dès que l'utilisateur quitte le tunnel — `pathname` est dans les
  // dépendances de l'effet, qui se rejoue à chaque navigation. On DIFFÈRE
  // l'affichage, on ne le supprime pas.
  const dansUnTunnel = (() => {
    try { return isTunnelPath(pathname); } catch (_) { return false; }
  })();

  useEffect(() => {
    // APP NATIVE (rejet Apple 5.1.2(i), juillet 2026) : JAMAIS de bandeau cookies
    // dans l'app. Apple assimile ce bandeau de consentement à du tracking exigeant
    // App Tracking Transparency. Gively ne fait aucun tracking publicitaire et la
    // mesure d'audience GA n'est même pas chargée dans l'app (cf. index.html) → il
    // n'y a rien à consentir, donc aucun bandeau. Le bandeau reste sur le web.
    if (typeof window !== "undefined" && window.__GIVELY_NATIVE__) return;
    // safeLocalStorage never throws on iOS Private Browsing (would white-screen).
    // Un choix de plus de 6 mois (recommandation CNIL) ne compte plus :
    // `lireConsentement` renvoie null et le bandeau est reproposé.
    const consent = lireConsentement(safeLocalStorage.getItem(COOKIE_CONSENT_KEY));
    if (consent) return;
    if (dansUnTunnel) {
      // On RETIRE aussi un bandeau déjà ouvert quand l'utilisateur ENTRE dans un
      // tunnel : sans cette ligne, celui apparu sur la page d'accueil resterait
      // collé au bas de l'écran de publication.
      setVisible(false);
      return;
    }
    // Petit délai pour ne pas bloquer le chargement initial
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [dansUnTunnel]);

  // Permet de rouvrir le bandeau depuis un lien « Gérer les cookies »
  // (footer) qui dispatch l'événement ci-dessous.
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener("gively:open-cookie-settings", reopen);
    return () => window.removeEventListener("gively:open-cookie-settings", reopen);
  }, []);

  const accept = () => {
    safeLocalStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: true,
      date: new Date().toISOString(),
      version: "1.1"
    }));
    applyConsent(true);
    setVisible(false);
  };

  const refuse = () => {
    safeLocalStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: false,
      date: new Date().toISOString(),
      version: "1.1"
    }));
    applyConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-slate-200 shadow-2xl px-4 pt-3 pb-3 md:p-6 animate-slide-up"
      // Safe-area iPhone (audit iOS 06/07/2026) : sans ce padding, les boutons
      // Accepter/Refuser étaient à ras de la home bar.
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4">
        <div className="flex-1">
          {/* Mobile (audit du 21/10/2026) : le bandeau masquait ~30 % de l'écran
              d'arrivée. Titre masqué et boutons sur une ligne ; le texte et les deux
              choix, de même poids (CNIL), restent identiques. */}
          <p className="hidden md:block font-semibold text-slate-800 text-sm md:text-base mb-1">
            Nous respectons votre vie privée
          </p>
          <p className="text-slate-500 text-xs md:text-sm">
            Gively utilise des cookies essentiels au fonctionnement du site (authentification, sécurité)
            et, <strong>avec votre accord</strong>, des cookies de mesure d'audience (Google Analytics)
            pour améliorer la plateforme. Jamais de publicité ciblée. Vous pouvez changer d'avis à tout
            moment avec le lien « Cookies », en bas de page.{" "}
            <Link to="/confidentialite" className="text-[#1DA1F2] underline">
              Politique de confidentialité
            </Link>
          </p>
        </div>
        <div className="flex gap-3 shrink-0 [&>button]:flex-1 md:[&>button]:flex-none">
          {/* MÊME STYLE pour les deux choix (29/10/2026) : refuser doit être
              aussi simple et aussi visible qu'accepter (CNIL). Un bouton plein
              face à un bouton grisé oriente le choix. */}
          <button
            onClick={refuse}
            className={BOUTON_CHOIX}
          >
            Refuser
          </button>
          <button
            onClick={accept}
            className={BOUTON_CHOIX}
          >
            Accepter
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
